import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { useOptimizedImage } from '@app/hooks/useOptimizedImage';
import { withAnalytics } from '@app/components/home_page/nested_views/advisor_preview/advisor_preview_with_analytics';
import classes from './classes.module.scss';
import Rating from '../../../advisor_avatar/rating/rating';
import AdvisorModes from '../../../home_page/nested_views/advisor_modes';
import PriceText from '../../../home_page/nested_views/price_text';
import { isXfmProgram, getCountryFlagUrl } from '../../../../config/util';
import AppLink from '../../../routes/app_link';
import ExpandedText from '../../../../../common/helpers/expanded_text';
import ReadingSinceAndCountItem, { readingSinceAndCountItemThemes } from '../readingSinceAndCountItem';
import { AdvisorModesThemes, liveModeState } from '../../../../config/const';
import { renderFreeMinutesLabel, renderPsiquicosFreeMinutesLabel, skeletonOrItem } from '../../../home_page/nested_views/nested_views_utils';
import { isPS } from '../../../../../common/config/utils';
import defaultAvatar from '../../../header/img/home_placeholder.png';

const RECOMMEND_AVATAR_SIZE: [number, number] = [160, 160];

export interface AdvisorRecommendPreviewProps {
  loading: boolean;
  advisor: Advisor.SingleItem;
  analytics: Analytic.Source;
  userXfmProgram: Advisor.UserXfmProgramProps;
  onAnalyticsClick: () => void;
}

const getadvisorSlug = (advisorSlug: string) => {
  if (advisorSlug !== undefined) return advisorSlug;
  return '';
};

const AdvisorRecommendPreview = ({
  advisor,
  loading,
  analytics,
  userXfmProgram,
  onAnalyticsClick
}: AdvisorRecommendPreviewProps) => {
  const { t } = useTranslation();

  const {
    profilePictureUrl,
    profilePictureDetails,
    id,
    yearJoined,
    readingsCount,
    slug,
    nickname,
    serviceTitle,
    xfmProgram,
    rating,
    isNewAdvisor,
    countryCode,
    localLiveModeMinPrice,
    localLiveModeMinPromoPrices,
    liveModes
  } = advisor;

  const flagUrl = getCountryFlagUrl(countryCode);

  const optimizedUrl = useOptimizedImage({
    imageUrl: profilePictureUrl,
    imageDetails: profilePictureDetails,
    desktopSize: RECOMMEND_AVATAR_SIZE,
    enableCaching: true
  });

  const profileImageUrl = loading ? defaultAvatar : (optimizedUrl || profilePictureUrl || defaultAvatar);

  const freeMinutesBadge = React.useMemo(() => {
    if (!isXfmProgram({ advisorXfmProgram: xfmProgram, userXfmProgram })) return null;
    return isPS() ? renderPsiquicosFreeMinutesLabel(t) : renderFreeMinutesLabel(t);
  }, [xfmProgram?.active, userXfmProgram?.credits, t]);

  const renderProfileImage = () => {
    if (loading) {
      return <Skeleton className={ classes.avatarImageSkeleton } />;
    }

    return (
      <img
        src={ profileImageUrl }
        alt={ nickname || '' }
        className={ classes.avatarImage }
        style={ !profilePictureUrl ? { objectFit: 'scale-down' } : undefined }
        suppressHydrationWarning
      />
    );
  };

  const renderAdvisorService = () => (
    <div className={ classes.advisorInfo }>
      <div className={ classes.contentBlock }>
        <div className={ classes.nameRatingRow }>
          <div className={ classes.nameTitleBlock }>
            <ExpandedText ellipsis customClasses={ { text: classes.advisorNickNameContainer, readMoreBtn: '' } }>
              <span className={ classes.advisrNickName } suppressHydrationWarning>
                { skeletonOrItem({ item: nickname, skeletonWidth: 120, maxChars: null }) as React.ReactNode }
              </span>
            </ExpandedText>
            { (loading || serviceTitle) && (
              <span className={ classes.serviceTitle } suppressHydrationWarning>
                { skeletonOrItem({ item: serviceTitle, skeletonWidth: 90, maxChars: null }) as React.ReactNode }
              </span>
            ) }
          </div>
          <div className={ classes.ratingWrap }>
            <Rating
              rating={ rating }
              isNewAdvisor={ false }
              theme={ AdvisorModesThemes.recommendedAdvisor }
              className={ classes.ratingPill }
            />
          </div>
        </div>

        <div className={ classes.priceBlock }>
          <PriceText
            localLiveModeMinPrice={ localLiveModeMinPrice }
            localLiveModeMinPromoPrices={ localLiveModeMinPromoPrices }
            liveModes={ liveModes }
            text={ t('home_page.from') }
            stacked
          />
        </div>

        <div className={ classes.metaBadgeRow }>
          <div className={ classes.readingsBlock }>
            { loading ? skeletonOrItem({ item: null, skeletonWidth: 90 }) as React.ReactNode : (
              <ReadingSinceAndCountItem
                readingsCount={ readingsCount }
                yearJoined={ yearJoined }
                isNewAdvisor={ isNewAdvisor }
                theme={ readingSinceAndCountItemThemes.recommendCarousel }
                loading={ false }
                plain
              />
            ) }
          </div>
          { freeMinutesBadge ? (
            <div className={ classes.freeMinWrap }>
              { freeMinutesBadge }
            </div>
          ) : null }
        </div>

        <div className={ classes.modesRow }>
          <AdvisorModes
            { ...advisor }
            loading={ loading }
            text={ t('home_page.from') }
            theme={ AdvisorModesThemes.recommendedAdvisor }
            showPrice={ false }
            modesOnly
          />
        </div>
      </div>
    </div>
  );

  const isOnline = !loading && advisor.status === liveModeState.online;

  const renderAdvisorContent = () => (
    <AppLink
      to={ {
        pathname: getadvisorSlug(slug),
        state: { ...analytics, startChatClickSource: (analytics || {}).clickSource || (analytics || {})['click source'] || 'busy carousel' }
      } }
      className={ classes.advisorRecommendContainer }
      key={ id }
      onClick={ onAnalyticsClick }
    >
      <div className={ classes.avatarHolder }>
        { renderProfileImage() }
        { !loading && flagUrl ? (
          <img src={ flagUrl } className={ classes.countryFlag } alt="country flag" suppressHydrationWarning />
        ) : null }
        { isOnline ? (
          <span className={ classes.onlineStatusBadge } suppressHydrationWarning>
            { t('advisor_profile.status_label_online') }
          </span>
        ) : null }
      </div>
      { renderAdvisorService() }
    </AppLink>
  );

  return renderAdvisorContent();
};

export default withAnalytics(AdvisorRecommendPreview);
